<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import DateSelector from '@/components/shared/DateSelector.vue';
import TimeSelector from '@/components/shared/TimeSelector.vue';
import colors from '@/utils/colors';
import {
  isToday,
  addDays,
  addMinutes,
  isAfter,
  areIntervalsOverlapping,
  parseISO,
  isSameDay,
} from 'date-fns';
import { format } from '@/utils/date-fns';
import { capitalize, cloneDeep } from 'lodash';
import { stringToMinutes, minutesToString } from '@/utils/helpers';
import { default as ButtonEl } from '@/components/shared/Button.vue';
import {
  IBookingResource,
  useBookingResourcesStore,
} from '@/stores/bookingResources';
import BaseOverlay from '@/overlays/BaseOverlay.vue';
import ExternalInstitutionTag from '@/components/cards/ExternalInstitutionTag.vue';
import DateTimeSummary from '@/views/booking/DateTimeSummary.vue';
import Keyboard from '@/components/shared/Keyboard.vue';
import { useInstitutionStore } from '@/stores/institution';

interface Props {
  resource: IBookingResource;
}

const props = withDefaults(defineProps<Props>(), {});

const bookingResourcesStore = useBookingResourcesStore();
const institutionStore = useInstitutionStore();

const emit = defineEmits(['close', 'next']);
const bookingTitle = ref<string>('');

const selected = ref({
  startDate: format(new Date(), 'yyyy-MM-dd'),
  startTime: null,
  endDate: format(new Date(), 'yyyy-MM-dd'),
  endTime: null,
});

const moduleColor = computed(() => {
  return colors['BOOKING'];
});

const startDateObject = computed(() => {
  return new Date(selected.value.startDate);
});

const endDateObject = computed(() => {
  return new Date(selected.value.endDate);
});

const futureUnavailabilities = computed((): any => {
  return cloneDeep(unavailable.value).filter((item: any) => {
    return isAfter(new Date(item.start), selectedStartDateTime.value);
  });
});

const unavailable = computed(() => {
  return props.resource.bookings!.map((booking: any) => {
    return {
      start: booking.start,
      end: booking.end,
    };
  });
});

const firstAvailableTime = computed(() => {
  return minutesToString(
    stringToMinutes(selected.value?.startTime || '00:00') + 15
  );
});

const selectedStartDateTime = computed(() => {
  const selectedStartDate = format(startDateObject.value, 'yyyy, MM, dd');
  return new Date(`${selectedStartDate}, ${selected.value.startTime}`);
});

const startTimeFrom = computed(() => {
  const date = new Date();
  const ms = 1000 * 60 * 15;
  const nearestQuarter = format(
    new Date(Math.floor(date.getTime() / ms) * ms),
    'HH:mm'
  );

  return isToday(startDateObject.value) ? nearestQuarter : '00:00';
});

const startTimeTo = computed(() => {
  return isSameDay(startDateObject.value, endDateObject.value)
    ? firstAvailableTime.value
    : '00:00';
});

const endDateTo = computed(() => {
  if (!futureUnavailabilities.value.length) return addDays(new Date(), 14);
  return new Date(futureUnavailabilities.value[0].start);
});

const endTimeTo = computed(() => {
  if (
    futureUnavailabilities.value.length &&
    isSameDay(
      new Date(selected.value.endDate),
      new Date(futureUnavailabilities.value[0].start)
    )
  ) {
    return format(new Date(futureUnavailabilities.value[0].start), 'HH:mm');
  }

  return '23:45';
});

const canGoNext = computed(() => {
  return (
    selected.value.startDate &&
    selected.value.startTime &&
    selected.value.endDate &&
    selected.value.endTime
  );
});

const allTimeSlots = computed(() => {
  const end = stringToMinutes('23:45');

  const dates = [];

  for (let i = 0; i < 30; i++) {
    dates.push(format(addDays(new Date(), i), 'yyyy-MM-dd'));
  }

  return dates.map((date) => {
    const allSlots = Array.from({ length: Math.floor(end / 15) + 1 }, (_, i) =>
      minutesToString(i * 15)
    );

    return {
      date,
      slots: allSlots.map((slot) => {
        return {
          time: slot,
          available: isTimeAvailable(date, slot),
        };
      }),
    };
  });
});

const futureTimeslots = computed(() => {
  const parsedEndDate = parseISO(selected.value.endDate);
  const parsedStartDate = parseISO(selected.value.startDate);
  const isEndingSameDay = isSameDay(parsedEndDate, parsedStartDate);
  const earliestSlotTime =
    selected.value.startTime === ''
      ? startTimeFrom.value
      : selected.value.startTime;

  if (!isEndingSameDay) {
    return getTimeSlots();
  }

  return getTimeSlots().filter((slot) => slot.time > earliestSlotTime!);
});

const unavailableDates = computed(() => {
  return cloneDeep(allTimeSlots.value)
    .filter((slot) => slot.slots.filter((slot) => slot.available).length < 1)
    .map((item) => item.date);
});

onMounted(() => {
  window?._paq?.push(['trackEvent', 'NewBooking', 'Viewed']);
});

function isTimeAvailable(date: any, slot: any) {
  if (!unavailable.value.length) return true;
  const slotDate = format(new Date(date), 'yyyy, MM, dd');
  const slotDateTime = new Date(`${slotDate}, ${slot}`);

  return (
    cloneDeep(unavailable.value).filter((range: any) => {
      const rangeStart = new Date(range.start);
      const rangeEnd = new Date(range.end);

      return areIntervalsOverlapping(
        { start: rangeStart, end: rangeEnd },
        { start: slotDateTime, end: addMinutes(slotDateTime, 15) }
      );
    }).length < 1
  );
}

function startDateSelected(amount: any) {
  const today = new Date();
  selected.value.startDate = format(addDays(today, amount), 'yyyy-MM-dd');
  selected.value.endDate = selected.value.startDate;
}

function endDateSelected(amount: any) {
  selected.value.endDate = format(
    addDays(new Date(selected.value.startDate), amount),
    'yyyy-MM-dd'
  );
}

function startTimeSelected(event: any) {
  selected.value.startTime = event;
  selected.value.endTime = null;
}

async function next() {
  bookingResourcesStore.setNewBooking({
    selectedStartDate: selected.value.startDate,
    selectedStartTime: selected.value.startTime,
    selectedEndDate: selected.value.endDate,
    selectedEndTime: selected.value.endTime,
    title: bookingTitle.value,
  });

  emit('next');
}

function getTimeSlots() {
  const index = allTimeSlots.value.findIndex(
    (slot) => slot.date === selected.value.startDate
  );

  return cloneDeep(allTimeSlots.value)[index].slots;
}

const onSummaryClicked = () => {
  selected.value.startDate = format(new Date(), 'yyyy-MM-dd');
  selected.value.startTime = null;

  selected.value.endDate = format(new Date(), 'yyyy-MM-dd');
  selected.value.endTime = null;
};
</script>

<template>
  <BaseOverlay
    :scroll-height="900"
    @close="emit('close')"
    :title="capitalize($t('booking.book')) + ' ' + resource.title"
    :header-image="resource.coverImage.source"
    titlePosition="center"
  >
    <template #content>
      <div
        class="w-full flex items-center justify-center border-b border-neutral-300 pb-12 mb-8"
      >
        <ExternalInstitutionTag
          :title="resource.institution.name"
          :image-source="resource.institution?.logo?.source || ''"
          class="shadow-smallBottom rounded-full border border-1 border-neutral-300 bg-neutral-0"
          v-if="institutionStore.isExternalInstitution(resource.institution.id)"
        />
      </div>

      <DateTimeSummary
        :start-date="selected.startDate"
        :start-time="selected.startTime"
        :end-date="selected.endDate"
        :end-time="selected.endTime"
        @summary-clicked="onSummaryClicked"
        class="mt-8 pb-8"
        :class="{
          'border-b border-neutral-300': selected.startTime && selected.endTime,
        }"
      />

      <DateSelector
        v-if="!selected.startDate || !selected.startTime"
        :color="moduleColor"
        :unavailable-dates="unavailableDates"
        @date-selected="startDateSelected"
      />

      <TimeSelector
        v-if="!selected.startDate || !selected.startTime"
        type="start"
        :selected-date="selected.startDate"
        :start-time="startTimeFrom"
        end-time="23:45"
        :color="moduleColor"
        :time-slots="getTimeSlots()"
        @time-selected="startTimeSelected"
        @deselect="selected.startTime = null"
        class="border-t border-neutral-300 mt-8 pt-8"
      />

      <DateSelector
        v-if="
          selected.startDate &&
          selected.startTime &&
          (!selected.endDate || !selected.endTime)
        "
        :color="moduleColor"
        :end-date="endDateTo"
        :initial-date="startDateObject"
        @date-selected="endDateSelected"
        class="border-t border-neutral-300 mt-8 pt-8"
      />

      <TimeSelector
        v-if="
          selected.startDate &&
          selected.startTime &&
          (!selected.endDate || !selected.endTime)
        "
        type="end"
        :selected-date="selected.endDate"
        :start-time="startTimeTo"
        :end-time="endTimeTo"
        :color="moduleColor"
        :time-slots="futureTimeslots"
        @time-selected="selected.endTime = $event"
        @deselect="selected.endTime = null"
        class="border-t border-neutral-300 mt-8 pt-8"
      />

      <Keyboard
        class="mt-12"
        v-if="
          selected.startDate &&
          selected.startTime &&
          selected.endDate &&
          selected.endTime
        "
        :title="$t('booking.addDescription')"
        @onChange="bookingTitle = $event"
      />
    </template>

    <template v-if="canGoNext" #actions>
      <ButtonEl
        text="modals.next"
        :icon="['fal', 'fa-arrow-right']"
        background="bg-green-400"
        textColor="text-neutral-0"
        class="mr-5 mt-8 shadow-xsm"
        rounded="full"
        @click="next"
      />
    </template>
  </BaseOverlay>
</template>
